import {
  Filters,
  FilterMenuItem,
  isValidEntityType,
  FilterMenuItemType,
} from "types";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import {
  getFilterTitle,
  FILTER_MENU_OPTIONS,
} from "helpers/utility/queryFilterHelpers";
import {
  isCustomMetadata,
  getCustomMetadataDisplayName,
} from "helpers/utility/customMetadataHelpers";
import {
  getCaptions,
  getFiltersMenuOptions,
  getSearchOptions,
} from "redux/SingleDataset/selectors";
import useQueryParam from "hooks/useQueryParam";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import { ENTITY_TYPE_PARAMETER } from "helpers/constants/miscellaneous";
import classNames from "classnames";

interface SingleOptionProps {
  name: string;
  icon: JSX.Element;
  disabled?: boolean;
  tooltipContent?: string;
  onSelect: () => void;
}

const SingleOption = ({
  icon,
  name,
  disabled = false,
  tooltipContent = "",
  onSelect,
}: SingleOptionProps) => {
  return (
    <div
      className={classNames(styles.filter, {
        [styles.disabled]: !!disabled,
      })}
      onClick={onSelect}
      data-tooltip-id={tooltipContent}
      data-tooltip-content={tooltipContent}
      data-testid={`single-filter-option-${name}`}
    >
      {tooltipContent && <TooltipWrapper id={tooltipContent} />}
      <div className={styles.icon}>{icon}</div>
      <span className={styles.name}>{name}</span>
    </div>
  );
};

interface FilterMenuProps {
  onSelect: (type: FilterMenuItemType, disabled: boolean) => void;
}

// Helper function to get column class for any filter type
const getColumnClass = (filterType: FilterMenuItemType) => {
  const filterToColumnMap: Record<Filters, string> = {
    [Filters.FILE]: styles.column1,
    [Filters.FOLDER]: styles.column1,
    [Filters.LABELS]: styles.column1,
    [Filters.CAPTION]: styles.column1,
    [Filters.USER_TAGS]: styles.column1,

    [Filters.ISSUES]: styles.column2,
    [Filters.OUTLIERS]: styles.column2,
    [Filters.MISLABELS]: styles.column2,
    [Filters.UNIQUENESS]: styles.column2,
    [Filters.DUPLICATES]: styles.column2,
    [Filters.LABEL_OUTLIER]: styles.column2,
    [Filters.SIZE]: styles.column2,
  };

  if (isCustomMetadata(filterType)) {
    return styles.column3;
  }
  return filterToColumnMap[filterType as Filters];
};

const getDisabledTextByFilterType = (
  filterType: FilterMenuItemType
): string => {
  const defaultDisabledText = "This filter is not available";

  if (isCustomMetadata(filterType)) {
    return "No values available for this field";
  }

  const disabledTextMap: Record<Filters, string> = {
    [Filters.CAPTION]: "No captions available for this dataset",
    [Filters.DUPLICATES]: "No duplicates available for this dataset",
    [Filters.FILE]: defaultDisabledText,
    [Filters.LABELS]: defaultDisabledText,
    [Filters.ISSUES]: defaultDisabledText,
    [Filters.FOLDER]: defaultDisabledText,
    [Filters.OUTLIERS]: defaultDisabledText,
    [Filters.USER_TAGS]: defaultDisabledText,
    [Filters.MISLABELS]: defaultDisabledText,
    [Filters.UNIQUENESS]: defaultDisabledText,
    [Filters.LABEL_OUTLIER]: defaultDisabledText,
    [Filters.SIZE]: defaultDisabledText,
  };

  return disabledTextMap[filterType as Filters];
};

// Helper function to determine how many columns are actually needed
const getActiveColumns = (filterOptions: FilterMenuItem[]) => {
  const activeColumns = new Set<string>();

  filterOptions.forEach((option) => {
    const columnClass = getColumnClass(option.type);
    activeColumns.add(columnClass);
  });

  return Math.max(1, activeColumns.size);
};

export default function FilterMenu({ onSelect }: FilterMenuProps) {
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  // TODO: get real entity param - VL-5486
  const entityTypeFromQueryParam =
    entityParam === null
      ? "IMAGES"
      : isValidEntityType(entityParam)
        ? entityParam
        : null;

  const hasCaptionSearch = useSelector((state) =>
    getSearchOptions(state, entityTypeFromQueryParam)
  ).captionsSearch;
  const hasCaptions = useSelector((state) => getCaptions(state))?.length > 0;
  const captionsExist = hasCaptions || hasCaptionSearch;

  const filterOptions = useSelector((state) =>
    getFiltersMenuOptions(
      state,
      FILTER_MENU_OPTIONS,
      captionsExist,
      entityTypeFromQueryParam
    )
  );

  // Calculate how many columns we actually need
  const activeColumnCount = getActiveColumns(filterOptions);

  return (
    <div className={styles.filterMenuWrapper}>
      <div
        className={styles.filterMenu}
        data-testid="filter-menu"
        style={{
          gridTemplateColumns: `repeat(${activeColumnCount}, max-content)`,
        }}
      >
        {filterOptions.map((option, index) => {
          const disabledText = option.disabled
            ? getDisabledTextByFilterType(option.type)
            : "";
          const columnClass = getColumnClass(option.type);
          const displayName = isCustomMetadata(option.type)
            ? getCustomMetadataDisplayName(option.type.field_name)
            : getFilterTitle(option.type, "filter");

          const handleSelect = () => {
            onSelect(option.type, !!option.disabled);
          };

          return (
            <div key={index} className={columnClass}>
              <SingleOption
                key={index}
                icon={option.icon}
                name={displayName}
                disabled={!!option.disabled}
                tooltipContent={disabledText}
                onSelect={handleSelect}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
